import React, { useEffect, Suspense } from 'react';
import styled from 'styled-components';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { ConfigProvider, theme as antTheme } from 'antd';
import { HelmetProvider } from 'react-helmet-async';

import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { SeasonalThemeProvider } from './contexts/SeasonalThemeContext';
import GlobalStyles from './theme/GlobalStyles';
import SeasonalDecorations from './components/SeasonalDecorations';
import { HomeSEO } from './components/SEO';
import LanguagePopup from './components/LanguagePopup';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Rooms from './components/Rooms';
import Activities from './components/Activities';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ChatWidget from './components/ChatWidget';
import { ToastProvider } from './components/shared/Toast';
import './scripts/updateTemplates'; // Exposes window.updateEmailTemplates()

// Code-split routes (loaded on demand)
const Login = React.lazy(() => import('./components/Login'));
const AdminPage = React.lazy(() => import('./components/AdminPage'));
const UnsubscribePage = React.lazy(() => import('./components/UnsubscribePage'));
const BookingForm = React.lazy(() => import('./components/BookingForm'));
const MinigamePage = React.lazy(() => import('./components/MinigamePage'));
const GuestProfilePage = React.lazy(() => import('./components/GuestProfilePage'));
const EventRegistrationPage = React.lazy(() => import('./components/EventRegistrationPage'));
const EventVerifyPage = React.lazy(() => import('./components/EventVerifyPage'));
const PrivacyPolicyPage = React.lazy(() => import('./components/PrivacyPolicyPage'));
const DeleteAccountPage = React.lazy(() => import('./components/DeleteAccountPage'));

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  font-size: 1.5rem;
  color: var(--brand-primary);

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid var(--border-light);
    border-top-color: var(--brand-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AppContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
  color: var(--text-primary);
  overflow-x: hidden;
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 100px;
  width: 50px;
  height: 50px;
  background: var(--brand-primary);
  color: var(--text-inverse);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 4px 15px var(--brand-shadow);

  &:hover {
    background: var(--brand-secondary);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px var(--brand-shadow-strong);
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 14px;
    right: 16px;
    bottom: 80px;
  }
`;

const AdminConfigWrapper = ({ children }) => {
  const { isDark, colors } = useTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#c4704b',
          colorSuccess: '#5a8a5e',
          colorWarning: '#d4a857',
          colorError: '#c45c5c',
          colorInfo: '#5a9a8a',
          colorLink: '#c4704b',
          borderRadius: 10,
          fontFamily: "'DM Sans', sans-serif",
          ...(isDark
            ? {
                colorBgContainer: '#2a2520',
                colorBgElevated: '#332e28',
                colorBorder: '#3d3630',
                colorBgLayout: '#1e1b18',
              }
            : {
                colorBgContainer: '#faf7f2',
                colorBgElevated: '#ffffff',
                colorBorder: '#e0d8cc',
                colorBgLayout: '#f3efe8',
              }),
        },
        components: {
          Layout: {
            siderBg: 'transparent',
          },
          Menu: {
            itemBg: colors.adminMenuItemBg,
            itemSelectedBg: colors.adminMenuSelectedBg,
            itemSelectedColor: colors.adminMenuSelectedColor,
            itemHoverBg: colors.adminMenuHoverBg,
            itemColor: colors.adminMenuItemColor,
            darkItemColor: colors.adminMenuItemColor,
            darkItemSelectedColor: colors.adminMenuSelectedColor,
            darkItemSelectedBg: colors.adminMenuSelectedBg,
            darkItemHoverBg: colors.adminMenuHoverBg,
          },
          Table: {
            cellPaddingBlockSM: 8,
            cellPaddingInlineSM: 8,
            headerBg: isDark ? '#2a2520' : '#f5f0e8',
            rowHoverBg: isDark ? 'rgba(212,132,95,0.06)' : 'rgba(196,112,75,0.04)',
          },
          Button: {
            borderRadius: 8,
          },
          Modal: {
            borderRadiusLG: 14,
          },
          Drawer: {
            borderRadiusLG: 14,
          },
          Card: {
            borderRadiusLG: 14,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

function SkipToContent() {
  return (
    <a href="#main-content" className="skip-to-content">
      Skip to main content
    </a>
  );
}

function HtmlLangUpdater() {
  const { currentLanguage } = useLanguage();
  useEffect(() => {
    document.documentElement.lang = currentLanguage === 'vi' ? 'vi' : 'en';
  }, [currentLanguage]);
  return null;
}

function App() {
  const [showScrollTop, setShowScrollTop] = React.useState(false);

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });

    // Handle scroll events
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const HomePage = () => (
    <>
      <HomeSEO />
      <LanguagePopup />
      <Navbar />
      <main id="main-content">
      <Hero />
      <About />
      <Rooms />
      <Activities />
      <Contact />
      <Footer />
      </main>
      <ScrollToTopButton $show={showScrollTop} onClick={scrollToTop} aria-label="Scroll to top">
        <i className="fas fa-chevron-up"></i>
      </ScrollToTopButton>
    </>
  );

  return (
    <HelmetProvider>
      <LanguageProvider>
        <HtmlLangUpdater />
        <ThemeProvider>
          <GlobalStyles />
          <SeasonalThemeProvider>
          <AuthProvider>
            <ToastProvider>
            <Router>
              <SeasonalDecorations />
              <AppContainer>
                <SkipToContent />
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />
                    <Route path="/booking" element={<BookingForm />} />
                    <Route path="/game/:gameId" element={<MinigamePage />} />
                    <Route path="/guest-profile" element={<GuestProfilePage />} />
                    <Route path="/event/:eventId" element={<EventRegistrationPage />} />
                    <Route path="/event/:eventId/verify" element={<EventVerifyPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/delete-account" element={<DeleteAccountPage />} />
                    <Route
                      path="/admin/*"
                      element={
                        <AdminConfigWrapper>
                          <ProtectedRoute>
                            <AdminPage />
                          </ProtectedRoute>
                        </AdminConfigWrapper>
                      }
                    />
                  </Routes>
                </Suspense>
                <ChatWidget />
              </AppContainer>
            </Router>
            </ToastProvider>
          </AuthProvider>
          </SeasonalThemeProvider>
        </ThemeProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;